import '../../styles/_end-of-video-panel.scss';
import '../../styles/_button.scss';

import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import creditProgressMessagesDefaults from '../../datasource/defaults/creditProgressMessages.json';
import { CleTimeRecord, EndOfSegmentTooltip, LaunchSegment } from '../../datasource/generated';
import { MenuOptions } from '../../enums/MenuOptions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FetchType, useAnalytics } from '../../hooks/useAnalytics';
import useChangeSegment from '../../hooks/useChangeSegment';
import useCpeExams from '../../hooks/useCpeExams';
import { useSegmentImages } from '../../hooks/useSegmentImages';
import Button from '../Button/Button';
import { ReactComponent as CreditStatusPending } from '../CreditProgressPanel/assets/credit-status-pending.svg';
import CreditProgressSaved from '../CreditProgressSaved/CreditProgressSaved';
import Loader from '../Loader/Loader';
import { ErrorContainer } from '../MainError/MainError';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import SegmentCard from '../SegmentCard/SegmentCard';
import SegmentProgress from '../SegmentProgress/SegmentProgress';
import { setIsExamStatusVisible, setLargePanelOpenCommand, setSmallPanelOpenCommand } from '../Store/menuSlice';
import { setLastVideoStartedFrom } from '../Store/playerSlice';
import TooltipRich from '../TooltipRich/TooltipRich';

export interface EndOfVideoButtonText {
    requestCredit: string,
    requestCreditAnyway: string,
    viewSegments: string,
    takeCpeExam: string,
    dontTakeCpeExam: string,
    playNext: string,
}

export type EndOfVideoViewState = 'standard' | 'live-return-later';
export type RequestCreditViewState = 'ready' | 'incomplete-program' | 'loading-credit-progress' | 'credit-progress-error';

export interface IncompleteProgramContent {
    title: string,
    body: string,
    prompt: string,
}

interface EndOfVideoBaseProps {
    title: string,
    nonTitleCta?: string,
    programFormat?: 'Live' | 'OD Multi-Segment' | 'OD Single-Segment' | 'CPE Segment',
    currentSegment?: LaunchSegment,
    nextSegment?: LaunchSegment,
    autoplay?: boolean,
    onPlaySegment?: () => void,
    buttonText: EndOfVideoButtonText,
    areExamsPassed?: boolean,
    isGroupcast: boolean,
    segments?: LaunchSegment[],
    cleTimeRecords?: CleTimeRecord[],
    progressSavedAt?: Date,
    infoTooltip?: EndOfSegmentTooltip | null,
    viewState: EndOfVideoViewState,
}

type EndOfVideoNonIncompleteProps = EndOfVideoBaseProps & {
    requestCreditViewState?: Exclude<RequestCreditViewState, 'incomplete-program'>,
    incompleteProgram?: IncompleteProgramContent,
};

type EndOfVideoIncompleteProps = EndOfVideoBaseProps & {
    requestCreditViewState: 'incomplete-program',
    incompleteProgram: IncompleteProgramContent,
};

export type IEndOfVideoProps = EndOfVideoNonIncompleteProps | EndOfVideoIncompleteProps;

const END_OF_VIDEO_ANALYTICS_EVENT = {
    requestCreditStart: 'request_credit_start',
    videoStart: 'video_start',
    viewSegments: 'player > segments',
    viewSchedule: 'player > schedule',
} as const;

const AUTOPLAY_COUNTDOWN_SECONDS = 60;

const DEFAULT_END_OF_SEGMENT_TOOLTIP = creditProgressMessagesDefaults.endOfSegmentTooltip;

const InfoIcon = () => (
    <svg width={20} height={20} viewBox='0 0 20 20' fill='none' aria-hidden={true} focusable={false}>
        <circle cx={10} cy={10} r={9} stroke='currentColor' strokeWidth={1.5} />
        <rect x={9.1} y={8.6} width={1.8} height={6} rx={0.9} fill='currentColor' />
        <circle cx={10} cy={5.6} r={1.1} fill='currentColor' />
    </svg>
);

const PauseIcon = () => (
    <svg width={16} height={16} viewBox='0 0 16 16' fill='none' aria-hidden={true} focusable={false}>
        <rect x={4} y={3} width={3} height={10} rx={1} fill='currentColor' />
        <rect x={9} y={3} width={3} height={10} rx={1} fill='currentColor' />
    </svg>
);

const PlayIcon = () => (
    <svg width={16} height={16} viewBox='0 0 16 16' fill='none' aria-hidden={true} focusable={false}>
        <path d='M4 3.2v9.6a.7.7 0 0 0 1.07.6l7.5-4.8a.7.7 0 0 0 0-1.2l-7.5-4.8A.7.7 0 0 0 4 3.2Z' fill='currentColor' />
    </svg>
);

export const EndOfVideoPanelBase = ({
    title,
    nonTitleCta,
    programFormat,
    currentSegment,
    nextSegment,
    autoplay,
    onPlaySegment,
    buttonText,
    areExamsPassed,
    isGroupcast,
    viewState,
    incompleteProgram,
    segments = [],
    cleTimeRecords,
    progressSavedAt,
    infoTooltip,
    requestCreditViewState = 'ready',
}: IEndOfVideoProps) => {
    const dispatch = useAppDispatch();
    const incompleteProgramTitleId = useId();
    const incompleteProgramDescriptionId = useId();
    const incompleteProgramPromptId = useId();

    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const playbackProgress = useAppSelector((state) => state.player.playbackProgress);
    const { changeSegment } = useChangeSegment();
    const { openExam, isGetExamsError } = useCpeExams();

    const { showBoundary } = useErrorBoundary();

    useEffect(() => {
        if (programFormat === 'CPE Segment')
            dispatch(setIsExamStatusVisible({ CpeEndOfVideo : true }));

        return () => {
            dispatch(setIsExamStatusVisible({ CpeEndOfVideo : false }));
        };
    }, [dispatch, programFormat]);

    const { progressSegments, topicIndex, currentTopicIndex } = useMemo(() => {
        const resolvedSegments = segments.length > 0 ? segments : launchDetails.segments ?? [];
        let nextSegmentIndex = -1;
        let completedSegmentIndex = -1;

        resolvedSegments.forEach((segment, index) => {
            if (nextSegmentIndex === -1 && segment.itemSk === nextSegment?.itemSk)
                nextSegmentIndex = index;

            if (completedSegmentIndex === -1 && segment.itemSk === currentSegment?.itemSk)
                completedSegmentIndex = index;
        });

        return {
            progressSegments: resolvedSegments,
            topicIndex: nextSegmentIndex,
            currentTopicIndex: completedSegmentIndex,
        };
    }, [currentSegment?.itemSk, launchDetails.segments, nextSegment?.itemSk, segments]);
    const topicCount = progressSegments.length;

    const didImagesFinishCaching = useAppSelector((state) => state.player.didSegmentThumbnailsFinishedCaching);
    const { getSegmentCachedImageUrl, getSegmentImageUrl } = useSegmentImages();

    const nextSegmentImageSrc = () => {
        if (nextSegment) {
            if (didImagesFinishCaching)
                return getSegmentCachedImageUrl(nextSegment.itemSk);
            else
                return getSegmentImageUrl(nextSegment.itemSk);
        }            
    };

    const shouldAutoplay = Boolean(autoplay && nextSegment?.itemSk);
    const [autoplayCountdown, setAutoplayCountdown] = useState<number>(AUTOPLAY_COUNTDOWN_SECONDS);
    const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
    const [isAutoplayCancelled, setIsAutoplayCancelled] = useState(false);
    const [isCreditRequestPending, setIsCreditRequestPending] = useState(false);
    const [isIncompleteProgramVisible, setIsIncompleteProgramVisible] = useState(false);

    const cancelAutoplay = useCallback(() => setIsAutoplayCancelled(true), []);
    const toggleAutoplayPause = useCallback(() => setIsAutoplayPaused(prev => !prev), []);

    const playNextSegment = useCallback(() => {
        const itemSk = nextSegment?.itemSk;
        if (itemSk === undefined) return;

        changeSegment({ itemSk });
        onPlaySegment && onPlaySegment(); // To close the large panel
    }, [changeSegment, nextSegment?.itemSk, onPlaySegment]);
    
    const { pushToDataLayer } = useAnalytics();

    const isCountdownRunning = shouldAutoplay && !isAutoplayPaused && !isAutoplayCancelled;

    useEffect(() => {
        if (!isCountdownRunning) return;

        const timerInterval = setInterval(() => {
            setAutoplayCountdown(prevCountdown => Math.max(prevCountdown - 1, 0));
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [isCountdownRunning]);

    useEffect(() => {
        if (!shouldAutoplay || isAutoplayCancelled || isAutoplayPaused || autoplayCountdown > 0) return;

        const startedFrom = 'auto_played';

        setIsAutoplayCancelled(true);
        dispatch(setLastVideoStartedFrom(startedFrom));
        playNextSegment();
        pushToDataLayer({ event: END_OF_VIDEO_ANALYTICS_EVENT.videoStart, additionalParams: { started_from: startedFrom } });
    }, [autoplayCountdown, dispatch, isAutoplayCancelled, isAutoplayPaused, playNextSegment, pushToDataLayer, shouldAutoplay]);

    const completedSegment = (
        <section className='end-of-video__completed-segment'>
            {<span className='paragraph-1 mb-0'>{currentSegment?.title}</span>}
            <CreditProgressSaved savedAt={progressSavedAt} />
        </section>
    );

    const continueToRequestCredit = useCallback(() => {
        setIsCreditRequestPending(false);
        setIsIncompleteProgramVisible(false);
        if (isGroupcast)
            dispatch(setSmallPanelOpenCommand(MenuOptions.Credit));
        else {
            dispatch(setLargePanelOpenCommand(MenuOptions.RequestCredit));
            pushToDataLayer({ event: END_OF_VIDEO_ANALYTICS_EVENT.requestCreditStart, fetchType: FetchType.Program });
        }
    }, [dispatch, isGroupcast, pushToDataLayer]);

    const onClickRequestCredit = () => {
        cancelAutoplay();
        setIsCreditRequestPending(true);
    };

    useEffect(() => {
        if (!isCreditRequestPending || requestCreditViewState === 'loading-credit-progress')
            return;

        if (requestCreditViewState === 'credit-progress-error') {
            showBoundary(new Error('Credit progress loading failed'));
            return;
        }

        if (requestCreditViewState === 'incomplete-program') {
            setIsCreditRequestPending(false);
            setIsIncompleteProgramVisible(true);
            return;
        }

        continueToRequestCredit();
    }, [continueToRequestCredit, isCreditRequestPending, requestCreditViewState, showBoundary]);

    const onClickViewSegments = () => {
        dispatch(setSmallPanelOpenCommand(MenuOptions.Segments));
        pushToDataLayer({
            event: programFormat === 'Live'
                ? END_OF_VIDEO_ANALYTICS_EVENT.viewSchedule
                : END_OF_VIDEO_ANALYTICS_EVENT.viewSegments,
        });
    };

    const onClickPlayNext = () => {
        cancelAutoplay();

        const startedFrom = 'end_of_video_promotion';

        dispatch(setLastVideoStartedFrom(startedFrom));
        playNextSegment();
        pushToDataLayer({ event: END_OF_VIDEO_ANALYTICS_EVENT.videoStart, additionalParams: { started_from: startedFrom } });
    };

    const onSegmentCardPress = () => {
        cancelAutoplay();

        const startedFrom = 'end_of_video_promotion';
        
        dispatch(setLastVideoStartedFrom(startedFrom));

        playNextSegment();
        pushToDataLayer({  event: END_OF_VIDEO_ANALYTICS_EVENT.videoStart, additionalParams: { started_from: startedFrom } });
    };

    const onSegmentProgressPress = (segment: LaunchSegment) => {
        const itemSk = segment.itemSk;
        if (itemSk === undefined) return;

        cancelAutoplay();

        const startedFrom = 'end_of_video_promotion';

        dispatch(setLastVideoStartedFrom(startedFrom));
        changeSegment({ itemSk });
        onPlaySegment && onPlaySegment();
        pushToDataLayer({ event: END_OF_VIDEO_ANALYTICS_EVENT.videoStart, additionalParams: { started_from: startedFrom } });
    };

    const requestCreditButton = (
        <button 
            className='fluid-button fluid-button--large fluid-button--green'
            onClick={onClickRequestCredit}
        >{buttonText.requestCredit}</button>
    );

    const tooltipCopy = infoTooltip?.copy || DEFAULT_END_OF_SEGMENT_TOOLTIP.copy;
    const tooltipTitle = infoTooltip?.title || DEFAULT_END_OF_SEGMENT_TOOLTIP.title;
    const tooltipLink = infoTooltip?.link?.href
        ? { text: infoTooltip.link.text || infoTooltip.link.href, href: infoTooltip.link.href }
        : DEFAULT_END_OF_SEGMENT_TOOLTIP.link;

    const infoTooltipControl = (
        <TooltipRich
            title={tooltipTitle}
            copy={tooltipCopy}
            link={tooltipLink}
            alignment='right'
            backgroundColor='#FFFFFF'
            textColor='#000000'
            width={335}
            height={160}
        >
            <button
                type='button'
                className='end-of-video__info-button'
                aria-label={tooltipTitle}
            >
                <InfoIcon />
            </button>
        </TooltipRich>
    );

    const autoplayPauseControl = shouldAutoplay && !isAutoplayCancelled && (
        <button
            type='button'
            className='end-of-video__autoplay-toggle'
            onClick={toggleAutoplayPause}
            aria-pressed={isAutoplayPaused}
            aria-label={isAutoplayPaused ? 'Resume autoplay countdown' : 'Pause autoplay countdown'}
        >
            {isAutoplayPaused ? <PlayIcon /> : <PauseIcon />}
        </button>
    );

    const autoplayCountdownBanner = shouldAutoplay && !isAutoplayCancelled && (
        <div className='end-of-video__autoplay-countdown'>
            <span className='text--medium-grey mb-0'>
                Next segment starts in <span className='text--white mb-0'>{autoplayCountdown}s</span>
            </span>
            {autoplayPauseControl}
        </div>
    );

    const renderStickyActions = (primaryCta: 'play-next' | 'request-credit', showPlayNext: boolean) => (
        <div className='end-of-video__sticky-actions'>
            <CreditProgressSaved savedAt={progressSavedAt} />
            <div className='end-of-video__sticky-actions-row'>
                <div className='end-of-video__cta-group'>
                    {primaryCta === 'play-next'
                        ? (
                            <>
                                {showPlayNext &&
                                    <button
                                        type='button'
                                        className='fluid-button fluid-button--large fluid-button--white'
                                        onClick={onClickPlayNext}
                                    >{buttonText.playNext}</button>
                                }
                                <button
                                    type='button'
                                    className='fluid-button fluid-button--large fluid-button--transparent'
                                    onClick={onClickRequestCredit}
                                >{buttonText.requestCredit}</button>
                            </>
                        )
                        : (
                            <>
                                <button
                                    type='button'
                                    className='fluid-button fluid-button--large fluid-button--green'
                                    onClick={onClickRequestCredit}
                                >{buttonText.requestCredit}</button>
                                {showPlayNext &&
                                    <button
                                        type='button'
                                        className='fluid-button fluid-button--large fluid-button--transparent'
                                        onClick={onClickPlayNext}
                                    >{buttonText.playNext}</button>
                                }
                            </>
                        )
                    }
                </div>
                {infoTooltipControl}
            </div>
        </div>
    );

    const cpeExamButtons = (
        <div className='end-of-video__cpe__btn-group'>
            <button
                className='fluid-button fluid-button--large fluid-button--white'
                onClick={openExam}
            >{buttonText.takeCpeExam}</button>
            <button
                className='fluid-button fluid-button--large fluid-button--transparent'
                onClick={onClickRequestCredit}
            >{buttonText.dontTakeCpeExam}</button>
        </div>
    );

    const seeScheduleButton = (
        <button
            type='button'
            className='fluid-button fluid-button--large fluid-button--white'
            onClick={onClickViewSegments}
        >See schedule</button>
    );

    const liveReturnLaterButtons = (
        <div className='end-of-video__live-actions'>
            {seeScheduleButton}
            <button
                type='button'
                className='fluid-button fluid-button--large fluid-button--transparent'
                onClick={onClickRequestCredit}
            >{buttonText.requestCredit}</button>
        </div>
    );

    if (isGetExamsError) {
        showBoundary(new Error('Exam loading failed'));
    }

    if (isCreditRequestPending && requestCreditViewState === 'loading-credit-progress') {
        return (
            <div className='end-of-video end-of-video--loading' aria-live='polite'>
                <Loader />
            </div>
        );
    }

    if (isIncompleteProgramVisible && incompleteProgram) {
        return (
            <div className='end-of-video end-of-video--incomplete-program'>
                <section
                    className='end-of-video__incomplete-program'
                    role='alert'
                    aria-labelledby={incompleteProgramTitleId}
                    aria-describedby={`${incompleteProgramDescriptionId} ${incompleteProgramPromptId}`}
                >
                    <div className='end-of-video__incomplete-program-heading'>
                        <CreditStatusPending width={24} height={24} aria-hidden={true} />
                        <h3 id={incompleteProgramTitleId} className='heading-5 mb-0'>{incompleteProgram.title}</h3>
                    </div>
                    <p id={incompleteProgramDescriptionId} className='paragraph-2 mb-0'>{incompleteProgram.body}</p>
                    <p id={incompleteProgramPromptId} className='end-of-video__incomplete-program-prompt paragraph-2 mb-0'>{incompleteProgram.prompt}</p>
                    <div className='end-of-video__incomplete-program-actions'>
                        <Button
                            buttonClass='fluid-button fluid-button--large fluid-button--white'
                            label={buttonText.requestCreditAnyway}
                            action={continueToRequestCredit}
                        />
                        <Button
                            buttonClass='fluid-button fluid-button--large fluid-button--transparent'
                            label={buttonText.viewSegments}
                            action={onClickViewSegments}
                        />
                    </div>
                    <CreditProgressSaved savedAt={progressSavedAt} />
                </section>
            </div>
        );
    }

    return (
        <div className='end-of-video'>

            <div className='large-panel__heading'>
                <h3 className={'heading-4 mb-0'}>{title}</h3>
            </div>
            

            {(programFormat === 'Live') &&
                    <section className='end-of-video__live'>
                        <CreditProgressSaved savedAt={progressSavedAt} />
                        {viewState === 'live-return-later' ? liveReturnLaterButtons : requestCreditButton}
                    </section>
            }

            {(programFormat === 'OD Single-Segment') &&
                    <section className='end-of-video__single-segment'>
                        <span className='paragraph-1 mb-0'>{currentSegment?.title}</span>
                        {renderStickyActions('request-credit', Boolean(nextSegment))}
                    </section>
            }

            {programFormat === 'OD Multi-Segment' &&
                <>
                    <section className='end-of-video__completed-segment end-of-video__completed-segment--multi'>
                        <span className='paragraph-1 mb-0'>{currentSegment?.title}</span>
                        {currentTopicIndex >= 0 && topicCount > 1 &&
                            <div className='end-of-video__segment-progress-details'>
                                <span className='paragraph-2 text--medium-grey'>
                                    Segment {currentTopicIndex + 1} of {topicCount}
                                </span>
                                {cleTimeRecords &&
                                    <SegmentProgress
                                        segments={progressSegments}
                                        currentSegmentItemSk={currentSegment?.itemSk}
                                        timeRecords={cleTimeRecords}
                                        playbackProgress={playbackProgress}
                                        onSelectSegment={onSegmentProgressPress}
                                    />
                                }
                            </div>
                        }
                    </section>
                    <div className='end-of-video__divider'/>
                    {
                        nextSegment ?
                            (<>
                                <section className='end-of-video__next-segment'>
                                    <div className='end-of-video__autoplay-banner'>
                                        {autoplayCountdownBanner}
                                        <SegmentCard
                                            segmentTitle={nextSegment.title ?? ''}
                                            subTitle={`Segment ${(topicIndex ?? 0) + 1} of ${topicCount}`}
                                            imageSrc={nextSegmentImageSrc() ?? ''}
                                            onSegmentCardPress={onSegmentCardPress}
                                        />
                                    </div>
                                </section>
                                {renderStickyActions('play-next', true)}
                            </>) :
                            (<section className='end-of-video__request-credit-cta'>
                                <CreditProgressSaved savedAt={progressSavedAt} />
                                <h3 className='heading-4 mb-0'>{nonTitleCta}</h3>
                                {requestCreditButton}
                            </section>)
                    }
                </>
            }

            {programFormat === 'CPE Segment' &&
                    <section className='end-of-video__cpe'>
                        {completedSegment}
                        {areExamsPassed ? requestCreditButton : cpeExamButtons}
                    </section>
            }
        </div>
    );
};

/**
 * Default export: component already wrapped with the Error Boundary HOC.
 * - Uses MainError as fallback (SmallPanel layout).
 * - No centering wrapper will be applied to custom fallbacks (per HOC behavior).
 */
const EndOfVideoPanel = withPLIErrorBoundary(EndOfVideoPanelBase, null, {
    errorContainer: ErrorContainer.LargePanel,
});

export default EndOfVideoPanel;
